import React from 'react';
import { useNavigate } from 'react-router-dom';

const h2Style = {
  color: '#fff',
  fontSize: 17,
  fontWeight: 600,
  marginTop: 36,
  marginBottom: 10,
  fontFamily: "var(--font-display, 'Fraunces', serif)",
  letterSpacing: '-0.2px',
};

const h3Style = {
  color: '#e2e8f0',
  fontSize: 14,
  fontWeight: 600,
  marginTop: 20,
  marginBottom: 6,
  fontFamily: "var(--font-body, 'Figtree', sans-serif)",
};

const pStyle = {
  color: '#b0b8c8',
  fontSize: 14,
  lineHeight: 1.75,
  margin: '0 0 12px',
  fontFamily: "var(--font-body, 'Figtree', sans-serif)",
};

const liStyle = {
  color: '#b0b8c8',
  fontSize: 14,
  lineHeight: 1.75,
  marginBottom: 6,
  fontFamily: "var(--font-body, 'Figtree', sans-serif)",
};

const strongStyle = { color: '#e2e8f0', fontWeight: 600 };

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div style={{
      background: '#0b1a29',
      minHeight: 'calc(100vh - 58px)',
      padding: 'clamp(32px, 5vw, 60px) 16px',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* Back link */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: '#e9a020', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
            marginBottom: 32, fontFamily: "var(--font-body, 'Figtree', sans-serif)",
          }}
        >
          ← Back to Dashboard
        </button>

        {/* Title */}
        <h1 style={{
          color: '#fff', fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700,
          marginBottom: 6, fontFamily: "var(--font-display, 'Fraunces', serif)",
          letterSpacing: '-0.5px',
        }}>
          Terms of Service
        </h1>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 40, fontFamily: "var(--font-body, 'Figtree', sans-serif)" }}>
          Last updated: 23 April 2026
        </p>

        <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: 36 }} />

        {/* 1 */}
        <h2 style={h2Style}>1. Acceptance of Terms</h2>
        <p style={pStyle}>
          By accessing or using FathomVoyage ("the Service"), you agree to be bound by these Terms of Service ("Terms").
          If you do not agree to these Terms, do not use the Service.
        </p>
        <p style={pStyle}>
          These Terms constitute a legally binding agreement between you ("User", "you") and FathomVoyage ("we", "us", "our").
        </p>

        {/* 2 */}
        <h2 style={h2Style}>2. Description of Service</h2>
        <p style={pStyle}>
          FathomVoyage provides maritime professionals with voyage cost intelligence tools, including bunker price monitoring,
          war risk zone visualisation, route cost comparison, and synthesised maritime news briefings. The Service aggregates
          data from publicly available sources and presents it in a structured interface.
        </p>

        {/* 3 */}
        <h2 style={h2Style}>3. Eligibility</h2>
        <p style={pStyle}>
          You must be at least 18 years of age to use the Service. By using the Service, you represent and warrant that you
          are of legal age and have the authority to enter into these Terms.
        </p>
        <p style={pStyle}>
          The Service is intended for maritime industry professionals, including chartering managers, operations directors,
          fleet managers, shipbrokers, and related roles. Non-professional use is permitted but not the primary focus.
        </p>

        {/* 4 */}
        <h2 style={h2Style}>4. Acceptable Use</h2>
        <p style={pStyle}>
          You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          {[
            'Use the Service for any illegal or unauthorised purpose',
            'Attempt to gain unauthorised access to any portion of the Service, other user accounts, or connected systems',
            'Use automated systems, bots, scrapers, or crawlers to access or extract data from the Service without express written permission',
            'Resell, redistribute, or republish data obtained from the Service without express written permission',
            'Reverse engineer, decompile, or disassemble any portion of the Service',
            'Impersonate any person or entity, or misrepresent your affiliation',
            'Interfere with or disrupt the Service or servers connected to the Service',
            'Upload viruses, malware, or other malicious code',
          ].map(item => <li key={item} style={liStyle}>{item}</li>)}
        </ul>

        {/* 5 */}
        <h2 style={h2Style}>5. Data Accuracy and Disclaimer of Warranties</h2>

        <h3 style={h3Style}>5.1 Informational Use Only</h3>
        <p style={pStyle}>
          All data, information, prices, risk assessments, route calculations, and other content provided through the Service
          ("Content") is provided for general informational and reference purposes only. The Service is <strong style={strongStyle}>NOT</strong> a
          substitute for professional advice, real-time market data, official regulatory guidance, or confirmed quotes from
          licensed brokers, insurers, suppliers, or maritime authorities.
        </p>

        <h3 style={h3Style}>5.2 No Warranty</h3>
        <p style={pStyle}>
          The Service and all Content are provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express
          or implied, including but not limited to:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          {[
            'Warranties of accuracy, completeness, or timeliness',
            'Warranties of merchantability or fitness for a particular purpose',
            'Warranties that the Service will be uninterrupted or error-free',
            'Warranties that defects will be corrected',
          ].map(item => <li key={item} style={liStyle}>{item}</li>)}
        </ul>
        <p style={pStyle}>
          Prices and rates displayed may be delayed, incomplete, or inaccurate. War risk zones are approximations based on
          publicly available references and do not constitute official insurance coverage boundaries. Incident reports are
          sourced from third-party news outlets and may not reflect all incidents or the most current information.
        </p>

        <h3 style={h3Style}>5.3 Commercial Decisions</h3>
        <p style={pStyle}>
          You must <strong style={strongStyle}>NOT</strong> rely on the Service as the sole basis for any commercial decision,
          fixture, voyage, purchase, insurance placement, or financial transaction. Always verify data independently with
          authoritative sources including your broker, insurer, bunker supplier, port agent, or other qualified professional.
        </p>

        {/* 6 */}
        <h2 style={h2Style}>6. Limitation of Liability</h2>
        <p style={pStyle}>
          To the maximum extent permitted by applicable law, in no event shall FathomVoyage, its officers, directors,
          employees, agents, or suppliers be liable for any indirect, incidental, special, consequential, or punitive
          damages, including without limitation:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          {[
            'Loss of profits, revenue, or business opportunities',
            'Loss or corruption of data',
            'Losses resulting from reliance on Content',
            'Losses resulting from commercial, chartering, or insurance decisions',
            'Business interruption or any other commercial damages',
          ].map(item => <li key={item} style={liStyle}>{item}</li>)}
        </ul>
        <p style={pStyle}>
          This limitation applies regardless of the legal theory on which the claim is based, even if FathomVoyage has been
          advised of the possibility of such damages.
        </p>
        <p style={pStyle}>
          In jurisdictions that do not allow the exclusion or limitation of liability for consequential or incidental damages,
          FathomVoyage's liability shall be limited to the maximum extent permitted by law.
        </p>
        <p style={pStyle}>
          Our aggregate liability to you for any claim arising out of or relating to the Service shall not exceed the greater
          of (a) the amount you paid to FathomVoyage in the 12 months preceding the claim, or (b) GBP 100.
        </p>

        {/* 7 */}
        <h2 style={h2Style}>7. Third-Party Content</h2>
        <p style={pStyle}>
          The Service aggregates and synthesises content from third-party sources. We do not endorse, verify, or assume
          responsibility for such content. Attribution to source publications is provided where applicable. Users are directed
          to original publishers for definitive reporting.
        </p>

        {/* 8 */}
        <h2 style={h2Style}>8. Intellectual Property</h2>
        <p style={pStyle}>
          All intellectual property rights in the Service, including the design, code, branding, layout, synthesis methodology,
          and original analysis, are owned by FathomVoyage or its licensors. You are granted a limited, non-exclusive,
          non-transferable licence to access and use the Service for its intended purpose.
        </p>
        <p style={pStyle}>
          You may not copy, reproduce, distribute, or create derivative works of the Service or its Content without express
          written permission.
        </p>

        {/* 9 */}
        <h2 style={h2Style}>9. User Accounts</h2>
        <p style={pStyle}>
          Accounts are created through LinkedIn Single Sign-On. You are responsible for maintaining the confidentiality of
          your LinkedIn credentials. We are not liable for unauthorised access to your account resulting from compromised
          third-party credentials.
        </p>
        <p style={pStyle}>
          You may delete your account at any time by contacting{' '}
          <a href="mailto:privacy@fathomvoyage.com" style={{ color: '#e9a020', textDecoration: 'none' }}>
            privacy@fathomvoyage.com
          </a>.
        </p>

        {/* 10 */}
        <h2 style={h2Style}>10. Pricing and Subscriptions</h2>
        <p style={pStyle}>
          Free features are provided at no cost. Paid subscriptions ("Pro", "Team") will be introduced in future releases.
          Pricing, features, and billing terms will be clearly displayed before any payment is required. All paid
          subscriptions are subject to a separate Subscription Agreement at the time of purchase.
        </p>

        {/* 11 */}
        <h2 style={h2Style}>11. Modifications to Service and Terms</h2>
        <p style={pStyle}>
          We reserve the right to modify, suspend, or discontinue the Service or any feature at any time without notice.
          We may also modify these Terms from time to time. Material changes will be notified via the Service or email.
          Continued use after changes constitutes acceptance of the modified Terms.
        </p>

        {/* 12 */}
        <h2 style={h2Style}>12. Termination</h2>
        <p style={pStyle}>
          We may suspend or terminate your access to the Service at any time for violation of these Terms or for any other
          reason at our discretion. Upon termination, your right to use the Service ceases immediately.
        </p>

        {/* 13 */}
        <h2 style={h2Style}>13. Governing Law and Jurisdiction</h2>
        <p style={pStyle}>
          These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising
          out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England
          and Wales.
        </p>

        {/* 14 */}
        <h2 style={h2Style}>14. Indemnification</h2>
        <p style={pStyle}>
          You agree to indemnify, defend, and hold harmless FathomVoyage from and against any claims, liabilities, damages,
          losses, and expenses (including reasonable legal fees) arising out of or related to your use of the Service, your
          violation of these Terms, or your violation of any rights of another party.
        </p>

        {/* 15 */}
        <h2 style={h2Style}>15. Severability</h2>
        <p style={pStyle}>
          If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue
          in full force and effect.
        </p>

        {/* 16 */}
        <h2 style={h2Style}>16. Entire Agreement</h2>
        <p style={pStyle}>
          These Terms, together with our{' '}
          <button
            onClick={() => window.location.href = '/privacy'}
            style={{
              background: 'none', border: 'none', padding: 0,
              color: '#e9a020', fontSize: 14, cursor: 'pointer',
              fontFamily: "var(--font-body, 'Figtree', sans-serif)",
            }}
          >
            Privacy Policy
          </button>
          , constitute the entire agreement between you and FathomVoyage regarding the Service and supersede all prior
          agreements and understandings.
        </p>

        {/* 17 */}
        <h2 style={h2Style}>17. Contact</h2>
        <p style={pStyle}>For questions regarding these Terms:</p>
        <p style={pStyle}>
          <strong style={strongStyle}>Email:</strong>{' '}
          <a href="mailto:privacy@fathomvoyage.com" style={{ color: '#e9a020', textDecoration: 'none' }}>
            privacy@fathomvoyage.com
          </a>
        </p>

        <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', margin: '36px 0 24px' }} />
        <p style={{ color: '#475569', fontSize: 12, fontFamily: "var(--font-body, 'Figtree', sans-serif)", fontStyle: 'italic' }}>
          These Terms are governed by the laws of England and Wales.
        </p>

      </div>
    </div>
  );
}
